import React from 'react';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { ResponsiveAmount, AccountCodeLink } from '../../ui/ResponsiveAmount';

interface BalanceWidgetProps {
  data: any;
}

/**
 * Classes SYSCOHADA pour le bilan
 */
const BALANCE_ACCOUNTS = {
  actifs: { code: '2', label: 'Immobilisations (Actifs)' },
  passifs: { code: '1', label: 'Capitaux (Passifs)' },
  stocks: { code: '3', label: 'Stocks' },
  tiers: { code: '4', label: 'Tiers' }
};

export function BalanceWidget({ data }: BalanceWidgetProps) {
  const balanceSheet = data?.financialPosition?.balanceSheet;
  
  if (!balanceSheet) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <p className="text-sm">Aucune donnée disponible</p>
      </div>
    );
  }

  const totalAssets = balanceSheet.totalAssets || 0;
  const totalLiabilities = balanceSheet.totalLiabilities || 0;
  const totalEquity = balanceSheet.totalEquity || 0;
  const netAssets = balanceSheet.netAssets || 0;
  const isPositive = netAssets >= 0;

  const items = [
    { label: 'Actifs', value: totalAssets, accountCode: BALANCE_ACCOUNTS.actifs.code },
    { label: 'Passifs', value: totalLiabilities, accountCode: BALANCE_ACCOUNTS.passifs.code },
    { label: 'Capitaux Propres', value: totalEquity, accountCode: BALANCE_ACCOUNTS.passifs.code },
    { label: 'Actif Net', value: netAssets, accountCode: null }
  ];

  return (
    <div className="space-y-3">
      {/* Header avec Actif Net */}
      <div className="flex items-center justify-between pb-2 border-b border-border">
        <span className="text-sm font-medium text-muted-foreground">Bilan</span>
        <div className={`flex items-center gap-1 ${
          isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
        }`}>
          {isPositive ? <ArrowUpRight className="h-3.5 w-3.5 flex-shrink-0" /> : <ArrowDownRight className="h-3.5 w-3.5 flex-shrink-0" />}
          <ResponsiveAmount value={netAssets} size="sm" className="font-semibold" />
        </div>
      </div>

      {/* Liste des items */}
      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.label} className="flex items-center justify-between text-sm gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-muted-foreground truncate">{item.label}</span>
              {item.accountCode && (
                <AccountCodeLink 
                  code={item.accountCode} 
                  variant="subtle"
                />
              )}
            </div>
            <ResponsiveAmount 
              value={item.value} 
              size="sm"
              colorize={item.value < 0}
              className="flex-shrink-0"
            />
          </div>
        ))}
      </div>

      {/* Barre de progression visuelle */}
      <div className="pt-2">
        <div className="flex h-2 rounded-full overflow-hidden bg-muted">
          <div 
            className="bg-blue-500 transition-all duration-500"
            style={{ 
              width: `${Math.min(100, (totalAssets / (totalAssets + totalLiabilities || 1)) * 100)}%` 
            }}
          />
          <div 
            className="bg-orange-500 transition-all duration-500"
            style={{ 
              width: `${Math.min(100, (totalLiabilities / (totalAssets + totalLiabilities || 1)) * 100)}%` 
            }}
          />
        </div>
        <div className="flex justify-between mt-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0"></span>
            <span className="truncate">Actifs</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0"></span>
            <span className="truncate">Passifs</span>
          </span>
        </div>
      </div>
    </div>
  );
}
